import type { MacroPackageRunReply } from '@playcastdotio/Messaging';

interface OwnedRun { packageSha256: string; startRequestId?: string; earlyTerminal?: MacroPackageRunReply;
  terminal?: MacroPackageRunReply }
const isTerminal = (reply: MacroPackageRunReply) => ['completed', 'cancelled', 'failed'].includes(reply.outcome)
  && reply.errorCode !== 'run_not_found';

/** Retains exact terminal acknowledgements after Native removes a run, within one control-session owner. */
export class LocalPackageRunHistory {
  private readonly runs = new Map<string, OwnedRun>();
  private readonly terminalOrder: string[] = [];
  public constructor(private readonly terminalLimit = 64) {}
  public claim(runId: string, packageSha256: string): void { this.runs.set(runId, { packageSha256 }); }
  public owns(runId: string): boolean { return this.runs.has(runId); }
  public terminal(runId: string): MacroPackageRunReply | undefined { return this.runs.get(runId)?.terminal; }
  public active(): string[] { return [...this.runs].filter(([, run]) => !run.terminal).map(([id]) => id); }
  public forget(runId: string): void { this.runs.delete(runId); }
  public clear(): void { this.runs.clear(); this.terminalOrder.length = 0; }
  public admitted(runId: string, reply: MacroPackageRunReply): void {
    const run = this.runs.get(runId);
    if (!run || reply.runId !== runId || !this.samePackage(run, reply)) throw new Error('local_package_native_identity_invalid');
    run.startRequestId = reply.requestId;
    if (run.earlyTerminal?.requestId === reply.requestId) this.retain(run, run.earlyTerminal);
    delete run.earlyTerminal;
    if (isTerminal(reply)) this.retain(run, reply);
  }
  public observe(reply: MacroPackageRunReply): void {
    const run = this.runs.get(reply.runId);
    if (!run || !isTerminal(reply) || !this.samePackage(run, reply) || run.terminal) return;
    if (!run.startRequestId) { run.earlyTerminal = reply; return; }
    if (reply.requestId === run.startRequestId) this.retain(run, reply);
  }
  /** A correlated status/cancel acknowledgement has its own request ID. Failed commands do not prove release. */
  public acknowledge(reply: MacroPackageRunReply): void {
    const run = this.runs.get(reply.runId);
    if (run && this.samePackage(run, reply) && ['completed', 'cancelled'].includes(reply.outcome)) this.retain(run, reply);
  }
  private samePackage(run: OwnedRun, reply: MacroPackageRunReply): boolean {
    // Completed loaded packages must report the exact SHA; pre-load failures/cancellation may omit it.
    return reply.revision === run.packageSha256 || (!reply.revision && reply.outcome !== 'completed');
  }
  private retain(run: OwnedRun, reply: MacroPackageRunReply): void {
    if (run.terminal) return;
    run.terminal = reply;
    this.terminalOrder.push(reply.runId);
    while (this.terminalOrder.length > this.terminalLimit) this.runs.delete(this.terminalOrder.shift()!);
  }
}
